import {createFileRoute, useNavigate} from '@tanstack/react-router'
import {FormEvent, useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {UserContext} from '../context/UserContext.tsx';
import {AlertsContext} from '../context/AlertsContext.tsx';
import {isAuthenticated} from '../utils/auth.ts';
import {
  acceptInvitation,
  checkUserExists,
  getInvitationDetails,
  InvitationDetails,
  signupFromInvitation
} from '../api/organizations.ts';
import Snackbars from '../components/Shared/Snackbars.tsx';
import TogglePasswordVisibility from '../components/Auth/TogglePasswordVisibility.tsx';

export const Route = createFileRoute('/invitations/$token')({
  component: InvitationPage
});

type InvitationState = 'loading' | 'show_accept' | 'show_signup' | 'redirect_login' | 'expired' | 'not_found' | 'already_accepted';

function InvitationPage() {
  const {token} = Route.useParams();
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {user, setUserToken} = useContext(UserContext);
  const {showAlert} = useContext(AlertsContext);

  const [state, setState] = useState<InvitationState>('loading');
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    async function loadInvitation() {
      try {
        const details = await getInvitationDetails(token);
        setInvitation(details);

        if (details.expired || details.status === 'expired') {
          setState('expired');
          return;
        }

        if (details.status === 'accepted') {
          setState('already_accepted');
          return;
        }

        if (isAuthenticated()) {
          setState('show_accept');
        } else {
          const userExists = await checkUserExists(details.email);
          if (userExists) {
            setState('redirect_login');
          } else {
            setState('show_signup');
          }
        }
      } catch {
        setState('not_found');
      }
    }

    loadInvitation();
  }, [token]);

  async function handleAccept() {
    setLoading(true);
    try {
      await acceptInvitation(token);
      showAlert({message: t('invitations.invitationAccepted'), type: 'success'});
      navigate({to: '/sessions'});
    } catch {
      showAlert({message: t('errors.genericResponseError'), type: 'error'});
    } finally {
      setLoading(false);
    }
  }

  async function handleSignup(event: FormEvent) {
    event.preventDefault();
    setLoading(true);

    const password = formRef.current?.password.value;
    const passwordCheck = formRef.current?.['password-check'].value;
    const firstName = formRef.current?.first_name?.value || '';
    const lastName = formRef.current?.last_name?.value || '';

    if (!password || !passwordCheck) {
      showAlert({message: t('forms.fillAllFields'), type: 'error'});
      setLoading(false);
      return;
    }

    if (password !== passwordCheck) {
      showAlert({message: t('forms.passwordsMustMatch'), type: 'error'});
      setLoading(false);
      return;
    }

    try {
      const response = await signupFromInvitation(token, password, firstName, lastName);
      await setUserToken(response.token);
      showAlert({message: t('invitations.invitationAccepted'), type: 'success'});
      navigate({to: '/sessions'});
    } catch {
      showAlert({message: t('errors.genericResponseError'), type: 'error'});
    } finally {
      setLoading(false);
    }
  }

  function handleRedirectToLogin() {
    navigate({to: '/', search: {returnTo: `/invitations/${token}`}});
  }

  if (state === 'loading') {
    return (
      <main className="flex flex-col items-center justify-center h-screen">
        <p className="text-[#778C99]">{t('invitations.loading')}</p>
      </main>
    );
  }

  if (state === 'not_found') {
    return (
      <main className="flex flex-col items-center justify-center h-screen text-center px-4">
        <h1 className="text-2xl font-bold text-[#363B43] mb-2">{t('invitations.invitationNotFound')}</h1>
        <p className="text-[#778C99]">{t('invitations.invitationNotFoundDescription')}</p>
      </main>
    );
  }

  if (state === 'expired') {
    return (
      <main className="flex flex-col items-center justify-center h-screen text-center px-4">
        <h1 className="text-2xl font-bold text-[#363B43] mb-2">{t('invitations.invitationExpired')}</h1>
        <p className="text-[#778C99]">{t('invitations.invitationExpiredDescription')}</p>
      </main>
    );
  }

  if (state === 'already_accepted') {
    return (
      <main className="flex flex-col items-center justify-center h-screen text-center px-4">
        <h1 className="text-2xl font-bold text-[#363B43] mb-2">{t('invitations.alreadyAccepted')}</h1>
        <button
          type="button"
          onClick={() => navigate({to: '/sessions'})}
          className="mt-4 cursor-pointer hover:opacity-80 bg-[#26A27F] text-white font-semibold py-3 px-6 rounded-full"
        >
          {t('common.continue')}
        </button>
      </main>
    );
  }

  if (state === 'redirect_login') {
    return (
      <main className="flex flex-col items-center justify-center h-screen text-center px-4">
        <h1 className="text-2xl font-bold text-[#363B43] mb-2">
          {t('invitations.joinOrganization', {organizationName: invitation?.organization_name})}
        </h1>
        <p className="text-[#778C99] mb-6">{t('invitations.signInToAccept')}</p>
        <button
          type="button"
          onClick={handleRedirectToLogin}
          className="cursor-pointer hover:opacity-80 bg-[#26A27F] text-white font-semibold py-3 px-6 rounded-full"
        >
          {t('forms.signIn')}
        </button>
      </main>
    );
  }

  if (state === 'show_accept') {
    return (
      <>
        <main className="flex flex-col items-center justify-center h-screen text-center px-4">
          <h1 className="text-2xl font-bold text-[#363B43] mb-2">
            {t('invitations.joinOrganization', {organizationName: invitation?.organization_name})}
          </h1>
          <p className="text-[#778C99] mb-6">
            {user?.email}
          </p>
          <button
            type="button"
            onClick={handleAccept}
            disabled={loading}
            className={`cursor-pointer hover:opacity-80 bg-[#26A27F] text-white font-semibold py-3 px-6 rounded-full ${loading ? 'opacity-50' : ''}`}
          >
            {loading ? t('common.loading') + '...' : t('invitations.acceptInvitation')}
          </button>
        </main>
        <Snackbars/>
      </>
    );
  }

  // show_signup state
  return (
    <>
      <main
        className="flex flex-col items-center justify-center xs:block xs:fixed xs:top-1/2 xs:left-1/2 xs:-translate-1/2 w-screen xs:w-[90%] md:w-[60%] xs:max-w-[768px] xs:min-w-[300px] xs:mx-auto bg-white xs:rounded-xl xs:shadow xs:py-8 h-screen xs:h-fit">
        <div className="w-fit h-fit mx-auto mb-8">
          <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" width="173" height="140">
            <path fill="none" stroke="#47BC97" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"
                  transform="translate(85.35 71.6)" d="M1.65 0L1.65 12.4" fillRule="evenodd"/>
            <path fill="none" stroke="#47BC97" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"
                  transform="translate(72.15 9.6)"
                  d="M13.2 0L16.5 0C16.5 0 29.700001 0 29.700001 13.226754L29.700001 37.200245C29.700001 37.200245 29.700001 50.426998 16.5 50.426998L13.2 50.426998C13.2 50.426998 0 50.426998 0 37.200245L0 13.226754C0 13.226754 0 0 13.2 0"
                  fillRule="evenodd"/>
            <path fill="none" stroke="#47BC97" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"
                  transform="translate(60.6 39.36)"
                  d="M0 0L0 7.4400001C0 21.136662 11.080953 32.240002 24.75 32.240002L28.049999 32.240002C41.719048 32.240002 52.799999 21.136662 52.799999 7.4400001L52.799999 0"
                  fillRule="evenodd"/>
            <path fill="#47BC97" transform="translate(0 104)"
                  d="M1.9006348 7.181818L7.5852275 7.181818L13.59375 21.841076L13.849432 21.841076L19.857954 7.181818L25.542547 7.181818L25.542547 29L21.076771 29L21.076771 14.801003L20.889172 14.801003L15.247192 28.897795L12.19599 28.897795L6.5540104 14.7499L6.3664107 14.7499L6.3664107 29L1.9006348 29L1.9006348 7.181818ZM36.573833 29L28.843786 29L28.843786 7.181818L36.642082 7.181818Q39.93182 7.181818 42.308613 8.4868612Q44.685406 9.7919035 45.969059 12.232616Q47.252708 14.673328 47.252708 18.073931Q47.252708 21.483021 45.967976 23.932222Q44.683239 26.381426 42.29047 27.690712Q39.897697 29 36.573833 29ZM33.454548 25.045588L36.377743 25.045588Q38.431854 25.045588 39.830612 24.314747Q41.229374 23.583906 41.939987 22.045488Q42.650604 20.507069 42.650604 18.073931Q42.650604 15.649115 41.937908 14.117188Q41.225212 12.58526 39.832775 11.860745Q38.440342 11.13623 36.39489 11.13623L33.454548 11.13623L33.454548 25.045588ZM51.187515 29L49.099445 29L57.03973 7.181818L59.170414 7.181818L67.110863 29L65.022629 29L58.176151 9.7671013L58.033993 9.7671013L51.187515 29ZM52.693142 20.633577L63.517002 20.633577L63.517002 22.423351L52.693142 22.423351L52.693142 20.633577ZM80.94323 16.230059L79.21022 16.713123Q78.958038 15.981534 78.487541 15.386774Q78.017044 14.792014 77.264229 14.438621Q76.511414 14.085227 75.403458 14.085227Q73.74852 14.085227 72.67968 14.875327Q71.610847 15.665427 71.610847 16.911877Q71.610847 17.966564 72.340607 18.620001Q73.070366 19.273438 74.593803 19.653465L77.065392 20.264204Q79.128494 20.76841 80.156631 21.851562Q81.184769 22.934715 81.184769 24.582331Q81.184769 25.963778 80.415886 27.039772Q79.647011 28.115767 78.2798 28.728338Q76.912582 29.34091 75.105164 29.34091Q72.708145 29.34091 71.152748 28.261335Q69.597351 27.181763 69.167557 25.136309L70.985794 24.681873Q71.333855 26.14139 72.36541 26.888878Q73.396957 27.636364 75.076698 27.636364Q76.958855 27.636364 78.091614 26.78767Q79.224373 25.938976 79.224373 24.667559Q79.224373 23.67679 78.563858 22.998552Q77.903351 22.320312 76.554031 21.997215L73.883514 21.358009Q71.756332 20.846647 70.731857 19.742188Q69.707382 18.637728 69.707382 16.997103Q69.707382 15.64412 70.444214 14.612571Q71.181046 13.581021 72.468361 12.995084Q73.755676 12.409146 75.403458 12.409146Q77.654823 12.409146 79.004227 13.430042Q80.35363 14.450939 80.94323 16.230059ZM95.826775 16.230059L94.093765 16.713123Q93.841576 15.981534 93.371078 15.386774Q92.900581 14.792014 92.147766 14.438621Q91.394958 14.085227 90.287003 14.085227Q88.632057 14.085227 87.563225 14.875327Q86.494385 15.665427 86.494385 16.911877Q86.494385 17.966564 87.224144 18.620001Q87.953903 19.273438 89.477341 19.653465L91.948929 20.264204Q94.012032 20.76841 95.040169 21.851562Q96.068306 22.934715 96.068306 24.582331Q96.068306 25.963778 95.299431 27.039772Q94.530556 28.115767 93.163338 28.728338Q91.79612 29.34091 89.988708 29.34091Q87.59169 29.34091 86.036293 28.261335Q84.480896 27.181763 84.051094 25.136309L85.869331 24.681873Q86.2174 26.14139 87.248947 26.888878Q88.280495 27.636364 89.960243 27.636364Q91.8424 27.636364 92.975159 26.78767Q94.10791 25.938976 94.10791 24.667559Q94.10791 23.67679 93.447403 22.998552Q92.786888 22.320312 91.437569 21.997215L88.767059 21.358009Q86.63987 20.846647 85.615395 19.742188Q84.590919 18.637728 84.590919 16.997103Q84.590919 15.64412 85.327759 14.612571Q86.06459 13.581021 87.351906 12.995084Q88.639221 12.409146 90.287003 12.409146Q92.538368 12.409146 93.887772 13.430042Q95.237175 14.450939 95.826775 16.230059ZM99.744293 29L99.744293 12.636364L101.64776 12.636364L101.64776 29L99.744293 29ZM100.71092 9.823864Q100.12782 9.823864 99.708839 9.4232788Q99.289856 9.0226936 99.289856 8.460227Q99.289856 7.8977604 99.708176 7.4971757Q100.12649 7.096591 100.70959 7.096591Q101.2927 7.096591 101.71176 7.4971757Q102.13082 7.8977604 102.13082 8.460227Q102.13082 9.0226936 101.71243 9.4232788Q101.29403 9.823864 100.71092 9.823864ZM117.17055 16.230059L115.43754 16.713123Q115.18536 15.981534 114.71486 15.386774Q114.24436 14.792014 113.49155 14.438621Q112.73873 14.085227 111.63078 14.085227Q109.97584 14.085227 108.907 14.875327Q107.83817 15.665427 107.83817 16.911877Q107.83817 17.966564 108.56792 18.620001Q109.29768 19.273438 110.82112 19.653465L113.29271 20.264204Q115.35581 20.76841 116.38395 21.851562Q117.41209 22.934715 117.41209 24.582331Q117.41209 25.963778 116.6432 27.039772Q115.87433 28.115767 114.50712 28.728338Q113.1399 29.34091 111.33248 29.34091Q108.93546 29.34091 107.38007 28.261335Q105.82467 27.181763 105.39487 25.136309L107.21311 24.681873Q107.56117 26.14139 108.59273 26.888878Q109.62428 27.636364 111.30402 27.636364Q113.18617 27.636364 114.31893 26.78767Q115.45169 25.938976 115.45169 24.667559Q115.45169 23.67679 114.79118 22.998552Q114.13067 22.320312 112.78135 21.997215L110.11084 21.358009Q107.98365 20.846647 106.95918 19.742188Q105.9347 18.637728 105.9347 16.997103Q105.9347 15.64412 106.67153 14.612571Q107.40836 13.581021 108.69568 12.995084Q109.98299 12.409146 111.63078 12.409146Q113.88214 12.409146 115.23154 13.430042Q116.58095 14.450939 117.17055 16.230059ZM127.50858 12.636364L127.50858 14.284146L119.69614 14.284146L119.69614 12.636364L127.50858 12.636364ZM122.13927 8.715909L124.04272 8.715909L124.04272 24.724487Q124.04272 25.754206 124.39429 26.334816Q124.74585 26.915428 125.31049 27.155128Q125.87511 27.394831 126.51432 27.394831Q126.89418 27.394831 127.15877 27.345142Q127.42336 27.295454 127.62228 27.238525L128.03409 28.957386Q127.7571 29.060425 127.34878 29.150978Q126.94045 29.241533 126.34386 29.241533Q125.29983 29.241533 124.33569 28.783436Q123.37157 28.32534 122.75542 27.4198Q122.13927 26.514259 122.13927 25.178923L122.13927 8.715909ZM135.88643 29.369373Q134.40211 29.369373 133.17871 28.79051Q131.95532 28.211647 131.22914 27.107271Q130.50296 26.002897 130.50296 24.426191Q130.50296 23.211702 130.96106 22.382484Q131.41916 21.553267 132.26077 21.020597Q133.1024 20.487926 134.25471 20.178978Q135.40703 19.870028 136.79547 19.696077Q138.16626 19.525623 139.12149 19.401278Q140.07672 19.276934 140.57393 18.999945Q141.07115 18.722956 141.07115 18.105059L141.07115 17.721535Q141.07115 16.04196 140.0768 15.077826Q139.08246 14.113692 137.2216 14.113692Q135.44615 14.113692 134.33461 14.893139Q133.22308 15.672585 132.77563 16.727272L130.97171 16.073919Q131.5257 14.738581 132.5058 13.932501Q133.48592 13.12642 134.70041 12.767783Q135.9149 12.409146 137.16484 12.409146Q138.10583 12.409146 139.11974 12.655923Q140.13365 12.902699 141.00723 13.504617Q141.8808 14.106534 142.4277 15.175454Q142.97461 16.244373 142.97461 17.891991L142.97461 29L141.07115 29L141.07115 26.414717L140.95746 26.414717Q140.61655 27.139149 139.94713 27.81922Q139.27771 28.49929 138.26389 28.934332Q137.25006 29.369373 135.88643 29.369373ZM136.14212 27.636364Q137.65489 27.636364 138.76285 26.966948Q139.8708 26.297529 140.47098 25.180754Q141.07115 24.063976 141.07115 22.721647L141.07115 20.363581Q140.86507 20.565996 140.3662 20.725798Q139.86731 20.885597 139.22627 21.004616Q138.58524 21.123634 137.94254 21.208862Q137.29984 21.29409 136.79547 21.358009Q135.41052 21.528465 134.42691 21.89068Q133.4433 22.252897 132.92487 22.888521Q132.40643 23.524147 132.40643 24.511419Q132.40643 26.002897 133.46994 26.81963Q134.53345 27.636364 136.14212 27.636364ZM149.40628 18.772728L149.40628 29L147.50298 29L147.50298 12.636364L149.34952 12.636364L149.34952 15.207331L149.51997 15.207331Q150.09525 13.950229 151.31506 13.186762Q152.53488 12.423295 154.33531 12.423295Q155.99008 12.423295 157.24011 13.112188Q158.49014 13.801081 159.18968 15.132757Q159.88924 16.464434 159.88924 18.403353L159.88924 29L157.98593 29L157.98593 18.517046Q157.98593 16.507048 156.86374 15.331676Q155.74156 14.156305 153.85242 14.156305Q152.56685 14.156305 151.56367 14.713778Q150.5605 15.271252 149.9834 16.30821Q149.40628 17.345171 149.40628 18.772728ZM170.83817 12.636364L170.83817 14.284146L163.02571 14.284146L163.02571 12.636364L170.83817 12.636364ZM165.46884 8.715909L167.37231 8.715909L167.37231 24.724487Q167.37231 25.754206 167.72388 26.334816Q168.07544 26.915428 168.64006 27.155128Q169.2047 27.394831 169.8439 27.394831Q170.22375 27.394831 170.48834 27.345142Q170.75293 27.295454 170.95186 27.238525L171.36368 28.957386Q171.08669 29.060425 170.67836 29.150978Q170.27003 29.241533 169.67345 29.241533Q168.62941 29.241533 167.66528 28.783436Q166.70114 28.32534 166.08499 27.4198Q165.46884 26.514259 165.46884 25.178923L165.46884 8.715909Z"/>
          </svg>
        </div>

        <h1 className="text-xl font-bold text-[#363B43] text-center mb-2">
          {t('invitations.createAccountToJoin', {organizationName: invitation?.organization_name})}
        </h1>

        <form ref={formRef} onSubmit={handleSignup} method="post" className="grid gap-4 group w-full mt-6">
          <div className="relative w-4/5 mx-auto space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <label htmlFor="first_name"
                       className="text-sm text-[#778C99] font-medium">{t('forms.firstNameLabel')}</label>
                <div className="mt-2">
                  <input type="text" name="first_name" id="first_name" autoComplete="given-name"
                         placeholder={t('forms.firstNamePlaceholder')}
                         className="outline-1 outline-gray-300 text-[15px] bg-white w-full py-3 px-4 rounded-xl"
                         disabled={loading}
                  />
                </div>
              </div>
              <div className="flex-1">
                <label htmlFor="last_name"
                       className="text-sm text-[#778C99] font-medium">{t('forms.lastNameLabel')}</label>
                <div className="mt-2">
                  <input type="text" name="last_name" id="last_name" autoComplete="family-name"
                         placeholder={t('forms.lastNamePlaceholder')}
                         className="outline-1 outline-gray-300 text-[15px] bg-white w-full py-3 px-4 rounded-xl"
                         disabled={loading}
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="text-sm text-[#778C99] font-medium">{t('forms.emailLabel')}</label>
              <div className="mt-2">
                <input type="email" name="email" id="email"
                       value={invitation?.email || ''}
                       className="outline-1 outline-gray-300 text-[15px] bg-gray-100 w-full py-3 px-4 rounded-xl cursor-not-allowed"
                       disabled={true}
                       readOnly
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="text-sm text-[#778C99] font-medium">{t('forms.passwordLabel')}</label>
              <div className="mt-2 relative">
                <input type="password" name="password" id="password" autoComplete="off"
                       placeholder={t('forms.passwordPlaceholder')}
                       className="outline-1 outline-gray-300 text-[15px] bg-white w-full py-3 px-4 rounded-xl"
                       disabled={loading}
                />
                <TogglePasswordVisibility id="password"/>
              </div>
            </div>

            <div>
              <label htmlFor="password-check"
                     className="text-sm text-[#778C99] font-medium">{t('forms.passwordCheckLabel')}</label>
              <div className="mt-2 relative">
                <input type="password" name="password-check" id="password-check"
                       placeholder={t('forms.passwordPlaceholder')}
                       className="outline-1 outline-gray-300 text-[15px] bg-white w-full py-3 px-4 rounded-xl"
                       disabled={loading}
                />
                <TogglePasswordVisibility id="password-check"/>
              </div>
            </div>

            <button type="submit"
                    className={`mt-6 cursor-pointer hover:opacity-80 w-full text-center bg-[#26A27F] disabled:bg-[#B2E9D2] text-white font-semibold py-4 rounded-full ${loading ? 'opacity-50' : ''}`}
                    disabled={loading}
            >
              {loading ? t('common.loading') + '...' : t('forms.signUp')}
            </button>
          </div>
        </form>
      </main>
      <Snackbars/>
    </>
  );
}
